import { SelectDropDown } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import FieldHelpIcon from '../FieldHelpIcon'

const propTypes = {
  /**
   * id of the multi select
   */
  id: PropTypes.string.isRequired,
  /**
   * Title of the field. Must be a FormattedMessage id
   */
  title: PropTypes.string,
  /**
   * options of the multi select
   */
  options: PropTypes.arrayOf(PropTypes.shape).isRequired,
  /**
   * Callback called when an option is selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Selected values
   */
  values: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /**
   * validation errors messages
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Class of the container wrapper
   */
  containerClassName: PropTypes.string,
  /**
   * Class to style the title
   */
  titleClassName: PropTypes.string,
  /**
   * Optional content rendered next to the field title
   */
  titleAction: PropTypes.node,
  /**
   * Class to override default SelectDropDown styles
   */
  className: PropTypes.string,
  /**
   * Passed to react-select: prefix for inner BEM classes (e.g. `{prefix}__menu-list`).
   * Use with local/global CSS to tweak the menu without `styles` objects.
   */
  classNamePrefix: PropTypes.string,
  /**
   * Indicates if field is required
   */
  required: PropTypes.bool,
  /**
   * If true, displays label and input side-by-side (like PropertyField)
   */
  inline: PropTypes.bool,
  /**
   * If false, hides the clear (×) button on the select
   */
  isClearable: PropTypes.bool,
  /** Optional `FormattedMessage` id for a help tooltip next to the title */
  titleTooltipId: PropTypes.string,
}

const defaultProps = {
  title: null,
  values: null,
  errors: [],
  containerClassName: '',
  titleClassName: '',
  titleAction: null,
  className: '',
  classNamePrefix: undefined,
  required: false,
  inline: false,
  isClearable: true,
  titleTooltipId: null,
}

const getFlatennedOptions = (options) => {
  // normal options array
  if (!options?.[0]?.options) {
    return options
  }

  // grouped options
  const result = []

  options?.forEach((option) => {
    result.push(...option.options)
  })

  return result
}

function SingleInputSelect(props) {
  const {
    id,
    title,
    titleTooltipId,
    options,
    onChange,
    values,
    errors,
    containerClassName,
    titleClassName,
    titleAction,
    required,
    className,
    classNamePrefix,
    inline,
    isClearable,
    ...rest
  } = props

  const toOption = useMemo(() => {
    const result = {}

    getFlatennedOptions(options)?.forEach((option) => {
      result[option.value] = option
    })

    return result
  }, [options])

  const selectedOption = toOption[values] ?? null
  const hasTitleColumn = Boolean(title || titleAction)

  if (inline) {
    const { style, ...restWithoutStyle } = rest
    return (
      <Row className={clsx('mb-2 align-items-center', containerClassName)}>
        {hasTitleColumn && (
          <Col sm="4" className={clsx('d-flex align-items-center text-bold text-muted', titleClassName)}>
            {title && (
              <>
                <FormattedMessage id={title} />
                {titleTooltipId && <FieldHelpIcon tooltipId={titleTooltipId} />}
                {required && <span className="font-size-regular text-color-danger">*</span>}
              </>
            )}

            {titleAction && <span className="ml-2 d-inline-flex align-items-center">{titleAction}</span>}
          </Col>
        )}
        <Col sm={hasTitleColumn ? '8' : '12'} style={{ marginLeft: hasTitleColumn ? '-4px' : '0' }}>
          <SelectDropDown
            id={id}
            value={selectedOption}
            isClearable={isClearable}
            options={options}
            onChange={(option) => onChange(option?.value ?? null)} // if option is cleared should be null (undefined gets overwritten in the form)
            className={clsx(errors?.length && 'select-error-border', className)}
            classNamePrefix={classNamePrefix}
            {...restWithoutStyle}
          />
          {errors?.map((errorMessage) => (
            <div key={errorMessage} className="text-color-danger mt-2">
              {errorMessage}
            </div>
          ))}
        </Col>
      </Row>
    )
  }

  return (
    <div className={clsx('mb-3', containerClassName)}>
      {(title || titleAction) && (
        <div className="d-flex align-items-center mb-2">
          {title && (
            <p className={clsx('mb-0', titleClassName)}>
              <FormattedMessage id={title} />
              {titleTooltipId && <FieldHelpIcon tooltipId={titleTooltipId} />}
              {required && <span className="font-size-regular text-color-danger">*</span>}
            </p>
          )}

          {titleAction && <div className="ml-2 d-inline-flex align-items-center">{titleAction}</div>}
        </div>
      )}

      <SelectDropDown
        id={id}
        value={selectedOption}
        isClearable={isClearable}
        options={options}
        onChange={(option) => onChange(option?.value ?? null)} // if option is cleared should be null (undefined gets overwritten in the form)
        className={clsx(errors?.length && 'select-error-border', className)}
        classNamePrefix={classNamePrefix}
        {...rest}
      />

      {errors?.map((errorMessage) => (
        <div key={errorMessage} className="text-color-danger mt-2">
          {errorMessage}
        </div>
      ))}
    </div>
  )
}

SingleInputSelect.propTypes = propTypes
SingleInputSelect.defaultProps = defaultProps

export default SingleInputSelect
